/**
 * MusicCommunityNode — the "Music ↔ Community" 4-card section.
 *
 * Single source of truth, mounted on:
 *   • /SoundLife (SoundLifeHubPage) — hub Music ↔ Community section
 *   • Site footer (Footer.jsx, just above "THE WALL: DRIVEN BY SOUND")
 *
 * Cards:
 *   1. We point fans to community  → /map
 *   2. Quality, Well Sourced Resources → /resources
 *   3. The Skoolie Foundation       → /partners/skoolie
 *   4. H.O.P.E. Victims Advocacy    → /resources/abuse
 */
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

const MusicCommunityNode = ({ context = "page" }) => {
  const sectionBg = context === "footer" ? "bg-[#FDF6EC] rounded-2xl" : "bg-[#FDF6EC]";
  return (
    <section
      className={`py-14 md:py-20 ${sectionBg}`}
      data-testid="music-community-node"
    >
      <div className="max-w-6xl mx-auto px-6">
        <div className="mb-10 text-center">
          <span className="inline-block uppercase tracking-[0.3em] text-xs md:text-sm font-bold text-[#B45309] mb-2">
            Section · The whole wall
          </span>
          <h2 className="font-['Playfair_Display'] text-4xl md:text-5xl font-semibold text-[#1A1A1A] leading-tight">
            Music ↔ Community
          </h2>
          <p
            className="font-['Caveat','Comic_Sans_MS',cursive] text-2xl md:text-3xl mt-2 text-[#B45309]"
            style={{ transform: "rotate(-0.5deg)", display: "inline-block" }}
          >
            Together we reach people for ground-level orgs.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6 items-start">
          {/* Left column — "We point fans to community" + Resource Library */}
          <div className="flex flex-col gap-6">
            <Link
              to="/map"
              className="group block bg-white rounded-2xl border border-[#E2E0DC] overflow-hidden hover:shadow-lg hover:border-[#2E5C55]/40 transition-all"
              data-testid="mc-card-point-fans"
            >
              <div className="aspect-[16/9] bg-[#F0EBE2] overflow-hidden border-b border-[#E2E0DC]">
                <img
                  src="https://customer-assets.emergentagent.com/job_211279cf-e69f-4285-b28c-6209ba7e3f92/artifacts/3ma9mw10_get%20in%20the%20field.png"
                  alt="Get In the Field — featured map view of community orgs"
                  className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500"
                  loading="lazy"
                />
              </div>
              <div className="p-7">
                <span className="text-xs uppercase tracking-[0.3em] font-bold text-[#2E5C55]">In the field</span>
                <h3 className="font-['Playfair_Display'] text-3xl md:text-4xl font-semibold text-[#1A1A1A] mt-2 mb-3 leading-tight">
                  We point fans to community
                </h3>
                <p className="text-base text-[#3a3a3a] leading-relaxed mb-4">
                  Every artist on THE WALL is a doorway to dozens of community orgs and the resources they steward. Open the map to see who&rsquo;s where, what they do, and how to get involved.
                </p>
                <span className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-[#2E5C55]">
                  Open Map &amp; Orgs <ArrowRight className="h-3.5 w-3.5" />
                </span>
              </div>
            </Link>

            <Link
              to="/resources"
              className="group block bg-white rounded-2xl border border-[#E2E0DC] overflow-hidden hover:shadow-lg hover:border-[#2E5C55]/40 transition-all"
              data-testid="mc-card-resource-library"
            >
              <div className="aspect-[16/9] bg-[#F0EBE2] overflow-hidden border-b border-[#E2E0DC]">
                <img
                  src="https://customer-assets.emergentagent.com/job_211279cf-e69f-4285-b28c-6209ba7e3f92/artifacts/3gd7hmjq_library%20scrnsht.png"
                  alt="Resource Library — Explore the Library by Filter or Scroll to see more"
                  className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500"
                  loading="lazy"
                />
              </div>
              <div className="p-7">
                <span className="text-xs uppercase tracking-[0.3em] font-bold text-[#2E5C55]">The Library</span>
                <h3 className="font-['Playfair_Display'] text-3xl md:text-4xl font-semibold text-[#1A1A1A] mt-2 mb-3 leading-tight">
                  Quality, Well Sourced Resources for ALL
                </h3>
                <p className="text-base text-[#3a3a3a] leading-relaxed mb-4">
                  From food scarcity resources to help navigating abuse, art publications to personal health, this resource guide provides.
                </p>
                <span className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-[#2E5C55]">
                  Open the Resource Library <ArrowRight className="h-3.5 w-3.5" />
                </span>
              </div>
            </Link>
          </div>

          {/* Right column — Community Spotlights stacked: Skoolie then H.O.P.E. */}
          <div className="flex flex-col gap-6">
            <Link
              to="/partners/skoolie"
              className="group block bg-white rounded-2xl border border-[#E2E0DC] p-7 hover:shadow-lg hover:border-[#B45309]/40 transition-all"
              data-testid="mc-card-skoolie"
            >
              <span className="text-xs uppercase tracking-[0.3em] font-bold text-[#B45309]">Community Spotlight</span>
              <h3 className="font-['Playfair_Display'] text-3xl md:text-4xl font-semibold text-[#1A1A1A] mt-2 mb-3 leading-tight">
                Washington's Skoolie Foundation
              </h3>
              <p className="text-base text-[#3a3a3a] leading-relaxed mb-4">
                Dignity on wheels &mdash; mobile shelter, hygiene haven, fighting hunger, and the Resource Hub that screens 57+ government benefits in 5 minutes. Touring artists can pick a Community Partner to lift each night.
              </p>
              <span className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-[#B45309]">
                See their SNAP <ArrowRight className="h-3.5 w-3.5" />
              </span>
            </Link>

            <Link
              to="/resources/abuse"
              className="group block bg-white rounded-2xl border border-[#E2E0DC] p-7 hover:shadow-lg hover:border-[#2E5C55]/40 transition-all"
              data-testid="mc-card-hope"
            >
              <span className="text-xs uppercase tracking-[0.3em] font-bold text-[#2E5C55]">Community Spotlight</span>
              <h3 className="font-['Playfair_Display'] text-3xl md:text-4xl font-semibold text-[#1A1A1A] mt-2 mb-3 leading-tight">
                H.O.P.E. Victims Advocacy Foundation
              </h3>
              <p className="text-base text-[#3a3a3a] leading-relaxed mb-4">
                Helping survivors of abuse, assault, and trafficking find their footing &mdash; trauma-informed advocacy, court accompaniment, and pathways to safe housing. A standing Community Partner for artists who want to make their nights matter.
              </p>
              <span className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-[#2E5C55]">
                See their resource node <ArrowRight className="h-3.5 w-3.5" />
              </span>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default MusicCommunityNode;
